/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  MAX_SUPPORT_ATTACHMENTS,
  fetchSupportAttachment,
  openSupportAttachment,
  repairSupportFilename,
  validateSupportAttachments,
} from "../../../services/SupportService";

const getFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

export function SupportReplyAttachmentFields({
  attachments = [],
  onChange,
  disabled = false,
  max = MAX_SUPPORT_ATTACHMENTS,
}) {
  const fileInputRef = useRef(null);
  const canAddMore = attachments.length < max;

  const handleChange = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    if (!picked.length) return;

    const merged = [...attachments, ...picked];
    const error = validateSupportAttachments(merged);
    if (error) {
      toast.error(error);
      return;
    }
    onChange(merged.slice(0, max));
  };

  const removeAt = (index) => {
    onChange(attachments.filter((_, i) => i !== index));
  };

  return (
    <div className="stk-reply-attachments">
      {canAddMore && (
        <button
          type="button"
          className="stk-reply-attachments__add"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
        >
          <i className="fas fa-paperclip" aria-hidden="true" />
          Add files
          <span className="stk-reply-attachments__hint">
            PNG, JPG, WEBP, GIF, PDF · {attachments.length}/{max}
          </span>
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        accept="image/*,.pdf,application/pdf"
        onChange={handleChange}
        disabled={disabled}
      />
      {attachments.length > 0 && (
        <ul className="stk-reply-attachments__list">
          {attachments.map((file, index) => (
            <li key={getFileKey(file)}>
              <span>{file.type === "application/pdf" ? "📄" : "🖼️"}</span>
              <span className="stk-reply-attachments__name">{file.name}</span>
              <button
                type="button"
                onClick={() => removeAt(index)}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function isImageAttachment(file) {
  return (
    String(file?.mimeType || "").startsWith("image/") ||
    /\.(png|jpe?g|gif|webp)$/i.test(file?.filename || file?.originalName || "")
  );
}

export function SupportAttachmentLink({ ticketId, file, className, children }) {
  const label = repairSupportFilename(file?.originalName || file?.filename || "attachment");

  const handleClick = async () => {
    if (!ticketId || !file?.filename) {
      toast.error("This attachment cannot be opened");
      return;
    }
    try {
      await openSupportAttachment(ticketId, file.filename);
    } catch {
      toast.error("This image could not be loaded");
    }
  };

  return (
    <button
      type="button"
      className={className || "stk-attachment-open"}
      onClick={handleClick}
      title={label}
    >
      {children || (
        <>
          <i className="fas fa-paperclip" aria-hidden="true" />
          {label}
        </>
      )}
    </button>
  );
}

function SupportAttachmentPreview({ ticketId, file }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [failed, setFailed] = useState(false);
  const label = repairSupportFilename(file?.originalName || file?.filename || "attachment");
  const showImage = isImageAttachment(file);

  useEffect(() => {
    if (!ticketId || !file?.filename) return undefined;
    let objectUrl = "";
    let cancelled = false;

    fetchSupportAttachment(ticketId, file.filename)
      .then(({ blobUrl: url }) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setBlobUrl(url);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ticketId, file?.filename]);

  const handleClick = async () => {
    if (blobUrl) {
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (!ticketId || !file?.filename) return;
    try {
      await openSupportAttachment(ticketId, file.filename);
    } catch {
      toast.error("This image could not be loaded");
    }
  };

  return (
    <button
      type="button"
      className={`stk-msg-attachments__item${showImage && blobUrl ? " is-image" : ""}`}
      onClick={handleClick}
      title={label}
    >
      {showImage && blobUrl ? (
        <img src={blobUrl} alt={label} className="stk-msg-attachments__image" />
      ) : (
        <i className={`fas fa-${showImage ? "image" : "file-pdf"}`} aria-hidden="true" />
      )}
      <span>{failed ? `${label} (unavailable)` : label}</span>
    </button>
  );
}

export function SupportMessageAttachments({
  attachments = [],
  ticketId,
  variant = "user",
}) {
  if (!attachments?.length) return null;

  return (
    <ul className={`stk-msg-attachments stk-msg-attachments--${variant}`}>
      {attachments.map((file) => (
        <li key={file.filename || file.originalName}>
          <SupportAttachmentPreview ticketId={ticketId} file={file} />
        </li>
      ))}
    </ul>
  );
}
